import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ColorMode } from '@/theme/theme';

export type ThemePreference = ColorMode | 'system';

interface UIState {
  /** What the user chose; `system` follows `prefers-color-scheme`. */
  themePreference: ThemePreference;
  /** Kept so persisted `wenkey.ui` still hydrates. The sidebar no longer uses an icon rail. */
  sidebarCollapsed: boolean;
  /** Mobile drawer open state (never persisted). */
  mobileNavOpen: boolean;
  aiAssistantOpen: boolean;
  /** Tenant selected by staff when browsing a customer's data. */
  impersonatedClienteId: string | null;
  /** Display label for the selected tenant, so the switcher survives a reload. */
  impersonatedClienteLabel: string | null;
}

interface UIActions {
  setThemePreference: (preference: ThemePreference) => void;
  toggleTheme: () => void;
  toggleSidebar: () => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setMobileNavOpen: (open: boolean) => void;
  toggleAiAssistant: () => void;
  setAiAssistantOpen: (open: boolean) => void;
  setImpersonatedClienteId: (clienteId: string | null, label?: string | null) => void;
}

export type UIStore = UIState & UIActions;

type PersistedUI = {
  themePreference: ThemePreference;
  sidebarCollapsed: boolean;
  impersonatedClienteId: string | null;
  impersonatedClienteLabel: string | null;
};

/**
 * Pure UI state, persisted so the app reopens the way the user left it.
 * `partialize` excludes transient flags (mobile drawer, assistant panel) so
 * they are not restored on load. The tenant switcher is persisted: staff keep
 * working on the same customer across navigation and reloads.
 */
export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      themePreference: 'system',
      sidebarCollapsed: true,
      mobileNavOpen: false,
      aiAssistantOpen: false,
      impersonatedClienteId: null,
      impersonatedClienteLabel: null,

      setThemePreference: (themePreference) => set({ themePreference }),

      toggleTheme: () => {
        const current = get().themePreference;
        // `system` toggles to the explicit opposite of what is on screen.
        if (current === 'system') {
          const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
          set({ themePreference: prefersDark ? 'light' : 'dark' });
          return;
        }
        set({ themePreference: current === 'dark' ? 'light' : 'dark' });
      },

      toggleSidebar: () => set({ sidebarCollapsed: !get().sidebarCollapsed }),
      setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
      setMobileNavOpen: (mobileNavOpen) => set({ mobileNavOpen }),
      toggleAiAssistant: () => set({ aiAssistantOpen: !get().aiAssistantOpen }),
      setAiAssistantOpen: (aiAssistantOpen) => set({ aiAssistantOpen }),
      setImpersonatedClienteId: (impersonatedClienteId, impersonatedClienteLabel) =>
        set({
          impersonatedClienteId,
          impersonatedClienteLabel: impersonatedClienteId ? impersonatedClienteLabel ?? null : null,
        }),
    }),
    {
      name: 'wenkey.ui',
      version: 4,
      partialize: (state): PersistedUI => ({
        themePreference: state.themePreference,
        sidebarCollapsed: state.sidebarCollapsed,
        impersonatedClienteId: state.impersonatedClienteId,
        impersonatedClienteLabel: state.impersonatedClienteLabel,
      }),
      migrate: (persisted): PersistedUI => {
        const state = (persisted ?? {}) as Partial<UIState>;
        return {
          themePreference: state.themePreference ?? 'system',
          sidebarCollapsed: true,
          impersonatedClienteId: state.impersonatedClienteId ?? null,
          impersonatedClienteLabel: state.impersonatedClienteLabel ?? null,
        };
      },
    },
  ),
);

export const selectThemePreference = (state: UIStore): ThemePreference => state.themePreference;
export const selectSidebarCollapsed = (state: UIStore): boolean => state.sidebarCollapsed;
